import Ajv from 'ajv'
import type { TFunction } from 'i18next'
import { listProperties, parse } from 'js-java-properties'

import appConfigSchema from '@/features/controller/configuration/models/appConfigSchema.json'
import resourceConfigSchema from '@/features/controller/configuration/models/resourceConfigSchema.json'
import type { ControllerConfigType } from '@/features/controller/configuration/types'
import { ControllerConfigTypeId, TextEditorLanguage } from '@/features/controller/configuration/types'

export interface ValidationResult {
  isValid: boolean
  errorMessage?: string
}

export const JSON_SCHEMA_MAP: Record<ControllerConfigTypeId, object | null> = {
  [ControllerConfigTypeId.RaspiProperties]: null,
  [ControllerConfigTypeId.AppJson]: appConfigSchema,
  [ControllerConfigTypeId.ResourceJson]: resourceConfigSchema,
}

/**
 * Sync JSON validation against a schema using Ajv
 */
function validateJsonWithSchema(jsonText: string, schema: object, t: TFunction): ValidationResult {
  try {
    const parsed = JSON.parse(jsonText)

    const ajv = new Ajv({ allErrors: true })
    const validate = ajv.compile(schema)
    const isValid = validate(parsed)

    if (!isValid && validate.errors) {
      return {
        isValid: false,
        errorMessage: t('controllers.configuration.schema_not_valid_configuration'),
      }
    }

    return { isValid: true }
  } catch {
    return { isValid: false, errorMessage: t('controllers.configuration.invalid_json') }
  }
}

/**
 * Java properties take the last occurrence of a repeated key, so an engineer who edits the first of
 * a duplicated pair sees the change saved and silently overridden by the line below. The format is
 * too loose to validate in general, but this one mistake is common and cheap to catch.
 *
 * listProperties yields every pair and already skips comments, blank lines and malformed lines, so
 * only real keys are counted.
 *
 * Throws on a malformed `\uXXXX` escape, the one thing the parser refuses to read at all.
 */
function findDuplicateKeys(propertiesText: string): string[] {
  const occurrences = new Map<string, number>()
  for (const { key } of listProperties(parse(propertiesText))) {
    occurrences.set(key, (occurrences.get(key) ?? 0) + 1)
  }
  return [...occurrences].filter(([, count]) => count > 1).map(([key]) => key)
}

/**
 * A text the parser cannot read is refused rather than passed on: Java's own Properties.load throws on
 * a malformed `\uXXXX` escape too, so the controller could never have read the file. Without the catch
 * the save threw and did nothing visible, which is worse than the generic backend error it replaced.
 */
function validateProperties(propertiesText: string, t: TFunction): ValidationResult {
  let duplicateKeys: string[]
  try {
    duplicateKeys = findDuplicateKeys(propertiesText)
  } catch {
    return { isValid: false, errorMessage: t('controllers.configuration.invalid_properties') }
  }

  if (duplicateKeys.length > 0) {
    return {
      isValid: false,
      errorMessage: t('controllers.configuration.duplicate_properties_keys', {
        keys: duplicateKeys.join(', '),
      }),
    }
  }
  return { isValid: true }
}

export function validateConfigText(configType: ControllerConfigType, value: string, t: TFunction): ValidationResult {
  if (configType.textEditorLanguage === TextEditorLanguage.ini) {
    return validateProperties(value, t)
  }
  if (configType.textEditorLanguage === TextEditorLanguage.json) {
    const schema = JSON_SCHEMA_MAP[configType.id]
    if (schema) {
      return validateJsonWithSchema(value, schema, t)
    } else {
      try {
        JSON.parse(value)
        return { isValid: true }
      } catch {
        return { isValid: false, errorMessage: t('controllers.configuration.invalid_json') }
      }
    }
  }
  // No validation for other types at the moment
  return { isValid: true }
}
